import React, { useState } from 'react';
import Header from './HeaderHome';
import Footer from './FooterHome';
import { contactAPI } from '../../services/api'; // adjust the import path as needed
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Contact = () => {
  // ── Contact information ──
  const contactInfo = [
    {
      icon: "📞",
      title: "Phone",
      details: ["080-26623007", "+91 98869 77701", "+91 95351 50008"],
    },
    {
      icon: "📧",
      title: "Email",
      details: ["info@venkatadrisevatrust.com"],
    },
    {
      icon: "📍",
      title: "Address",
      details: [
        "#65, 3rd Floor, SSR Archade,",
        "Gandhi Bazaar Main Road,",
        "Basavanagudi, Bangalore,",
        "Karnataka - 560004",
      ],
    },
  ];

  // ── Office hours ──
  const officeHours = [
    { day: "Monday - Friday", hours: "9:00 AM – 6:00 PM" },
    { day: "Saturday", hours: "9:00 AM – 2:00 PM" },
    { day: "Sunday", hours: "Closed" },
  ];

  // ── Form state ──
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // ── Handlers ──
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await contactAPI.submit(formData);
      toast.success('Your message has been sent successfully!');
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Contact form error:', error);
      const errMsg = error.response?.data?.message || 'Failed to send message. Please try again.';
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />

      {/* ========== HERO ========== */}
      <section className="contact-hero relative text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-block bg-primary/80 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-medium mb-4">
              <i className="fas fa-phone-alt mr-2"></i> Get in Touch
            </div>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-4">
              Contact Us
            </h1>
            <p className="text-xl md:text-2xl text-yellow-100/90 font-light">
              We'd love to hear from you – whether it's a question, a donation, or a partnership opportunity
            </p>
            <div className="w-24 h-1 bg-yellow-300 mx-auto mt-6 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* ========== CONTACT INFO ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {contactInfo.map((item, idx) => (
              <div
                key={idx}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-smooth text-center border-t-4 border-primary"
              >
                <div className="text-4xl mb-3">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-800">{item.title}</h3>
                {item.details.map((line, i) => (
                  <p key={i} className="text-gray-600 text-sm mt-1">
                    {line}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== OFFICE HOURS & MAP ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="vision-card rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-4 text-center">Find Us</h3>
              <div className="rounded-lg overflow-hidden h-80 md:h-96 w-full">
                <iframe
                  title="Venkatadri Seva Trust Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.956432158691!2d77.5755805152578!3d12.94179019086243!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae159d4b980d3d%3A0x7c3fd7c2c5a3e96f!2sGandhi%20Bazaar%20Main%20Rd%2C%20Basavanagudi%2C%20Bengaluru%2C%20Karnataka%20560004!5e0!3m2!1sen!2sin!4v1695193545000!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CONTACT FORM ========== */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Send Us a Message</h2>
              <div className="w-20 h-1 bg-primary mx-auto mt-3 rounded-full"></div>
              <p className="text-gray-600 mt-4 text-lg">
                We will get back to you within 24 hours.
              </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your full name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-1">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+91 9535150008"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="How can we help you?"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 font-medium mb-1">Message *</label>
                <textarea
                  rows="4"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Write your message here..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-white py-3 rounded-lg font-semibold hover:bg-primary-dark transition-smooth disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <i className="fas fa-paper-plane mr-2"></i>
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>

            <p className="text-gray-500 text-xs text-center mt-4">
              We respect your privacy. Your information will not be shared with third parties.
            </p>
          </div>
        </div>
      </section>

      {/* ========== FOLLOW US ========== */}
      <section className="py-16 bg-primary">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Follow Us</h2>
          <div className="w-20 h-1 bg-yellow-300 mx-auto mb-6 rounded-full"></div>
          <p className="text-yellow-100/90 text-lg mb-6">
            Stay connected with us on social media for the latest updates and stories.
          </p>
          <div className="flex justify-center gap-6 text-3xl">
            <a href="#" className="hover:text-yellow-300 transition"><i className="fab fa-facebook-f"></i></a>
            <a href="#" className="hover:text-yellow-300 transition"><i className="fab fa-twitter"></i></a>
            <a href="#" className="hover:text-yellow-300 transition"><i className="fab fa-instagram"></i></a>
            <a href="#" className="hover:text-yellow-300 transition"><i className="fab fa-youtube"></i></a>
            <a href="#" className="hover:text-yellow-300 transition"><i className="fab fa-linkedin-in"></i></a>
          </div>
        </div>
      </section>

      <ToastContainer />

      <Footer />
    </>
  );
};

export default Contact;